/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import noop from 'lodash/noop';
import Script from 'next/script';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

// https://legacydocs.hubspot.com/docs/methods/forms/advanced_form_options
export interface HubspotFormConfig {
  css?: string;
  cssClass?: string;
  cssRequired?: string;
  errorClass?: string;
  errorMessageClass?: string;
  formId: string;
  formInstanceId?: string;
  goToWebinarWebinarKey?: string;
  inlineMessage?: string;
  locale?: string;
  manuallyBlockedEmailDomain?: string[];
  onBeforeFormInit?: (context: HubspotFormConfig) => void;
  onBeforeFormSubmit?: (form: any, values: Record<string, unknown>) => void;
  onFormReady?: (form: any) => void;
  onFormSubmitted?: (
    form: any,
    data: {
      redirectUrl: string;
      submissionValues: Record<string, unknown>;
    },
  ) => void;
  pageId?: number | string;
  portalId: string;
  redirectUrl?: string;
  region: 'na1' | 'eu1';
  sfdcCampaignId?: string;
  submitButtonClass?: string;
  submitText?: string;
  target: string;
  translations?: Record<string, any>;
}

declare global {
  interface Window {
    hbspt: {
      forms: {
        create: (config: HubspotFormConfig) => void;
      };
    };
  }
}

export interface HubspotFormsContextValues {
  activeModal: string;
  isHubspotLoaded: boolean;
  setActiveModal: Dispatch<SetStateAction<string>>;
}

const HubspotFormsContext = createContext<HubspotFormsContextValues>({
  activeModal: '',
  isHubspotLoaded: false,
  setActiveModal: noop,
});

export const HubspotFormsProvider = ({ children }: PropsWithChildren) => {
  const [activeModal, setActiveModal] = useState<string>('');
  const [isHubspotLoaded, setIsHubspotLoaded] = useState<boolean>(false);

  const handleHubspotLoad = () => {
    setIsHubspotLoaded(true);
  };

  const value = useMemo(
    () => ({
      activeModal,
      isHubspotLoaded,
      setActiveModal,
    }),
    [activeModal, isHubspotLoaded],
  );

  return (
    <HubspotFormsContext.Provider value={value}>
      {children}
      <Script
        async
        onLoad={handleHubspotLoad}
        src="//js.hsforms.net/forms/embed/v2.js"
      />
    </HubspotFormsContext.Provider>
  );
};

export const useHubspotFormsContext = (): HubspotFormsContextValues =>
  useContext(HubspotFormsContext);
